/**
 * BaseCountdown 倒计时组件（H5 渲染端）
 *
 * 对应 B 端 market-topic 的 base-countdown 配置组件，渲染专题页 / 会员页配置的倒计时。
 * 配置数据来自 moduleJsonData.countdown.countdownData，字段：
 *  - endTime          倒计时结束时间
 *  - numberColor      数字颜色
 *  - unitColor        天时分秒颜色
 *  - bgImg            背景底图（沿宽度等比拉伸）
 *  - dayFrameImg / hourFrameImg / minuteFrameImg / secondFrameImg  各单位数字底图
 *  - fallbackImg      倒计时结束后的兜底图
 *
 * 时间基准：参考 ff5df3d，基于 /charge/user/serverTime 的公共 offset
 * 计算剩余时间，消除用户系统时间误差，且不受 setInterval 在后台被节流影响。
 */

import React, { useMemo, useState } from "react";
import { processOssImage } from "@/utils/image";
import { useServerNow } from "@/hooks/useServerTimeSync";
import { getRemainingSeconds } from "@/utils/server-time";
import style from "./index.module.less";

interface CountdownDataItem {
  key: string;
  val?: string;
}

const BaseCountdown: React.FC<{
  moduleJsonData?: {
    countdown?: {
      countdownData?: CountdownDataItem[];
    };
  };
}> = (props) => {
  const countdownData = props?.moduleJsonData?.countdown?.countdownData || [];

  const getVal = (key: string): string =>
    countdownData.find((item) => item.key === key)?.val || "";

  const numberColor = getVal("numberColor") || "#333333";
  const unitColor = getVal("unitColor") || "#333333";
  const bgImg = getVal("bgImg");
  const fallbackImg = getVal("fallbackImg");

  // 结束时间（epoch 毫秒）；兼容日期字符串与数字时间戳
  const endTime = useMemo<number>(() => {
    const raw = getVal("endTime");
    if (!raw) return 0;
    const asNum = Number(raw);
    if (!Number.isNaN(asNum) && String(asNum) === String(raw)) return asNum;
    const parsed = new Date(raw).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countdownData]);

  const now = useServerNow();

  // 数字底图展示尺寸（rem）：项目 postcss-pxtorem 以 rootValue:1 换算，
  // 设计稿 Npx 等价于 Nrem，故图片原始像素数值可直接作为 rem 数值使用，
  // 与其他元素共用同一套 750 设计稿基准的缩放规则
  const [frameImgSizeMap, setFrameImgSizeMap] = useState<
    Record<string, { width: string; height: string }>
  >({});

  const handleFrameImgLoad = (
    key: string,
    e: React.SyntheticEvent<HTMLImageElement>,
  ) => {
    const { naturalWidth, naturalHeight } = e.currentTarget;
    setFrameImgSizeMap((prev) => ({
      ...prev,
      [key]: {
        width: `${naturalWidth * 2}rem`,
        height: `${naturalHeight * 2}rem`,
      },
    }));
  };

  // 倒计时是否结束
  const isEnded = !!endTime && now >= endTime;

  // 展示上限：99天23时59分59秒
  const MAX_REMAIN_SECONDS = 99 * 24 * 3600 + 23 * 3600 + 59 * 60 + 59;

  // 剩余天时分秒：超出 [0, 99天23时59分59秒] 范围时固定展示极值
  const remain = useMemo(() => {
    const totalSeconds = Math.min(
      endTime ? getRemainingSeconds(endTime, now) : 0,
      MAX_REMAIN_SECONDS,
    );
    return {
      day: Math.floor(totalSeconds / (24 * 3600)),
      hour: Math.floor(totalSeconds / 3600) % 24,
      minute: Math.floor(totalSeconds / 60) % 60,
      second: totalSeconds % 60,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTime, now]);

  // 数值转两位数字数组
  const toDigits = (num: number): string[] =>
    String(num).padStart(2, "0").split("");

  const unitList = [
    {
      key: "day",
      text: "天",
      digits: toDigits(remain.day),
      frameImg: getVal("dayFrameImg"),
    },
    {
      key: "hour",
      text: "时",
      digits: toDigits(remain.hour),
      frameImg: getVal("hourFrameImg"),
    },
    {
      key: "minute",
      text: "分",
      digits: toDigits(remain.minute),
      frameImg: getVal("minuteFrameImg"),
    },
    {
      key: "second",
      text: "秒",
      digits: toDigits(remain.second),
      frameImg: getVal("secondFrameImg"),
    },
  ];

  // 倒计时结束且配置了兜底图：整个组件替换为兜底图
  if (isEnded && fallbackImg) {
    return (
      <div className={style["image-box"]}>
        <img src={processOssImage(fallbackImg)} />
      </div>
    );
  }

  return (
    <div
      className={[
        style["countdown-wrapper"],
        bgImg ? style["has-bg"] : style["no-bg-img"],
      ].join(" ")}
    >
      {/* 背景底图：正常文档流展示，宽度 100%，高度按图片原始比例自适应撑开容器 */}
      {bgImg && (
        <div className={style["image-box"]}>
          <img src={processOssImage(bgImg)} />
        </div>
      )}
      <div className={style["countdown-box"]}>
        {unitList.map((unit) => (
          <div className={style["countdown-unit"]} key={unit.key}>
            <div className={style["number-cell"]}>
              {/* 数字底图按原始像素尺寸乘以当前屏幕像素比换算展示，以单元格中心为锚点，不参与文字布局 */}
              {unit.frameImg && (
                <img
                  className={style["frame-img"]}
                  src={processOssImage(unit.frameImg)}
                  style={frameImgSizeMap[unit.key]}
                  onLoad={(e) => handleFrameImgLoad(unit.key, e)}
                />
              )}
              {/* 数字直接位于底图上层，保持固定单元格内的原有间距 */}
              {unit.digits.map((digit, dIdx) => (
                <span
                  className={style["number-digit"]}
                  key={dIdx}
                  style={{ color: numberColor }}
                >
                  {digit}
                </span>
              ))}
            </div>
            <span className={style["unit-text"]} style={{ color: unitColor }}>
              {unit.text}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default BaseCountdown;
